"use client"

/**
 * Client-side delivery of the chrome indexed-collection set (plan §0.16).
 *
 * The root layout (a server component) resolves the set once per request via
 * lib/collection-links.getIndexedChromeSlugs() and wraps every page in
 * IndexedChromeSlugsProvider. SiteHeader (a client component) and the footer's
 * GatedFooterLinkList read it through useIndexedChromeSlugs — so ALL ~60
 * per-page chrome render sites are gated without threading a prop through
 * each of them. An explicitly passed `indexedCollectionSlugs` prop on
 * SiteHeader/SiteFooter still wins over the context value.
 *
 * `null` context value = no set resolved (DB check failed with no cached
 * value) → the gate passes links through unchanged; see
 * lib/chrome-link-gate.keepIndexedCollectionLinks for the fail-open rationale.
 */

import Link from "next/link"
import { createContext, useContext, useMemo, type ReactNode } from "react"
import { keepIndexedCollectionLinks } from "@/lib/chrome-link-gate"

const IndexedChromeSlugsContext = createContext<readonly string[] | null>(null)

export function IndexedChromeSlugsProvider({
  slugs,
  children,
}: {
  slugs: readonly string[] | null
  children: ReactNode
}) {
  return (
    <IndexedChromeSlugsContext.Provider value={slugs}>
      {children}
    </IndexedChromeSlugsContext.Provider>
  )
}

/** The server-confirmed indexed chrome slugs, or null when no set resolved. */
export function useIndexedChromeSlugs(): readonly string[] | null {
  return useContext(IndexedChromeSlugsContext)
}

/**
 * The footer's link-column renderer, moved behind the client boundary so it
 * can read IndexedChromeSlugsContext while components/site-footer.tsx itself
 * stays a synchronous dual-tree (server AND client) component. Markup is
 * IDENTICAL to the footer's previous inline renderLinkList — only the gating
 * input changed. An explicit `indexedCollectionSlugs` prop (from a server
 * parent that resolved its own set) takes precedence over the context.
 */
export function GatedFooterLinkList({
  links,
  indexedCollectionSlugs,
  inline = false,
}: {
  links: { title: string; href: string }[]
  indexedCollectionSlugs?: readonly string[] | null
  /**
   * Horizontal wrap layout for the footer's rotating priority-page slot
   * (plan §0.136) instead of the default vertical column. Same gate, same link
   * styling — only the list layout differs.
   */
  inline?: boolean
}) {
  const contextSlugs = useIndexedChromeSlugs()
  const effectiveSlugs = indexedCollectionSlugs ?? contextSlugs
  const indexedSet = useMemo(
    () => (effectiveSlugs ? new Set(effectiveSlugs) : null),
    [effectiveSlugs]
  )
  return (
    <ul className={inline ? "flex flex-wrap gap-x-5 gap-y-2" : "space-y-2"}>
      {keepIndexedCollectionLinks(links, indexedSet).map((link) => (
        <li key={link.title}>
          <Link href={link.href} className="footer-column-link">
            {link.title}
          </Link>
        </li>
      ))}
    </ul>
  )
}
